import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { StyledClickable } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyWrapper } from "@waytek/MobiusOverrides/Link/Link";
import { ContentWrapper } from "@waytek/Overrides/Widgets/Basic/RichContent";
import { mediaQueriesBP } from "@waytek/Utilities/variables";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

interface ContentBox {
    fields: {
        rtfContent: string;
        backgroundColor: string;
        buttonLink1: LinkFieldValue;
        buttonLabel1: string;
        buttonVariant1: "primary" | "secondary" | "tertiary";
        newTab1: boolean;
        buttonLink2: LinkFieldValue;
        buttonLabel2: string;
        buttonVariant2: "primary" | "secondary" | "tertiary";
        newTab2: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        padding: number;
        contentBoxes: ContentBox[];
    };
}

export interface ContentBoxesStyles {
    wrapper?: InjectableCss;
    gridContainer?: InjectableCss;
    gridItem?: InjectableCss;
    box?: InjectableCss;
    rtfContent?: InjectableCss;
    buttonsWrapper?: InjectableCss;
}

export const contentBoxesStyles: ContentBoxesStyles = {
    gridContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr;
            gap: 30px;

            ${mediaQueriesBP.minMD} {
                grid-template-columns: repeat(2, 1fr);
            }
        `,
    },
    box: {
        css: css`
            height: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    rtfContent: {
        css: css`
            margin-block-end: 15px;

            ${StyledClickable} {
                display: initial;
            }

            ${TypographyWrapper} {
                display: initial;
                border-bottom: 0;

                & > ${TypographyStyle} {
                    display: initial;
                    border-bottom: 0;
                    font-size: inherit;
                    line-height: inherit;
                }

                &:hover {
                    text-decoration-style: double;
                }
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: auto;
            width: 100%;
            flex-direction: column;
            gap: 15px;

            ${mediaQueriesBP.minMD} {
                flex-direction: row;
            }
        `,
    },
};

const styles = contentBoxesStyles;

const ContentBoxes: React.FC<OwnProps> = ({ fields: { padding, contentBoxes } }) => {
    if (!contentBoxes || contentBoxes.length === 0) {
        return null;
    }
    const { isInShell } = useShellContext();
    const history = useHistory();

    const onClick = (url: string | undefined, newTab: boolean) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http") && isInShell) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            if (newTab) {
                const redirectWindow = window.open(url, "_blank");
                redirectWindow?.location;
            } else {
                window.location.href = url;
            }
        } else {
            /* eslint-disable no-lonely-if */
            if (newTab) {
                const redirectWindow = window.open(url, "_blank");
                redirectWindow?.location;
            } else {
                history.push(url);
            }
            /* eslint-enable no-lonely-if */
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.gridContainer}>
                {contentBoxes.map((box, index) => {
                    const mergedBoxStyles = {
                        ...styles.box,
                        css: css`
                            padding: ${padding || 40}px;
                            background-color: ${contentBoxes[index].fields.backgroundColor};
                            display: flex;
                            flex-direction: column;
                        `,
                    };

                    return (
                        <StyledWrapper key={index} {...mergedBoxStyles}>
                            {box.fields.rtfContent && (
                                <ContentWrapper
                                    className="wrapper"
                                    padding={0}
                                    backgroundColor="transparent"
                                    {...styles.rtfContent}
                                >
                                    {parse(box.fields.rtfContent, parserOptions)}
                                </ContentWrapper>
                            )}
                            <StyledWrapper {...styles.buttonsWrapper}>
                                {box.fields.buttonLink1?.value?.trim().length > 0 &&
                                    (box.fields.buttonLabel1 || box.fields.buttonLink1.value) && (
                                        <Button
                                            className="room-button"
                                            variant={box.fields.buttonVariant1}
                                            onClick={() => onClick(box.fields.buttonLink1.value, box.fields.newTab1)}
                                        >
                                            {box.fields.buttonLabel1 || box.fields.buttonLink1?.value}
                                        </Button>
                                    )}

                                {box.fields.buttonLink2?.value?.trim().length > 0 &&
                                    (box.fields.buttonLabel2 || box.fields.buttonLink2.value) && (
                                        <Button
                                            className="room-button"
                                            variant={box.fields.buttonVariant2}
                                            onClick={() => onClick(box.fields.buttonLink2.value, box.fields.newTab2)}
                                        >
                                            {box.fields.buttonLabel2 || box.fields.buttonLink2?.value}
                                        </Button>
                                    )}
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ContentBoxes,
    definition: {
        group: "Contact Us",
        displayName: "Content Boxes",
        fieldDefinitions: [
            {
                name: "padding",
                displayName: "Card Padding",
                editorTemplate: "TextField",
                defaultValue: "40px",
                fieldType: "General",
            },
            {
                name: "contentBoxes",
                displayName: "Content Boxes",
                editorTemplate: "ListField",
                fieldType: "Contextual",
                defaultValue: [],
                getDisplay: () => "Card",
                fieldDefinitions: [
                    {
                        name: "rtfContent",
                        displayName: "Rich Text Content",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "backgroundColor",
                        displayName: "Background Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "#f5f5f5",
                    },
                    {
                        name: "buttonLink1",
                        displayName: "Button 1 Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: false,
                    },
                    {
                        name: "buttonLabel1",
                        editorTemplate: "TextField",
                        displayName: "Button 1 Label",
                        defaultValue: "",
                        isVisible: widget => widget.fields.buttonLink1?.value?.trim().length > 0,
                        isRequired: true,
                    },
                    {
                        name: "buttonVariant1",
                        displayName: "Button 1 Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                        isVisible: widget => widget.fields.buttonLink1?.value?.trim().length > 0,
                    },
                    {
                        name: "newTab1",
                        displayName: "Button 1 - Open in a new tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.buttonLink1?.value?.trim().length > 0,
                    },
                    {
                        name: "buttonLink2",
                        displayName: "Button 2 Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: false,
                    },
                    {
                        name: "buttonLabel2",
                        editorTemplate: "TextField",
                        displayName: "Button 2 Label",
                        defaultValue: "",
                        isVisible: widget => widget.fields.buttonLink2?.value?.trim().length > 0,
                        isRequired: true,
                    },
                    {
                        name: "buttonVariant2",
                        displayName: "Button 2 Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "secondary",
                        isVisible: widget => widget.fields.buttonLink2?.value?.trim().length > 0,
                    },
                    {
                        name: "newTab2",
                        displayName: "Button 2 - Open in a new tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.buttonLink2?.value?.trim().length > 0,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
